import { useRef, type MouseEvent } from 'react';
import { useLocale } from '@/i18n/LocaleProvider';

const CALENDLY_URL = 'https://calendly.com/philip-gizmodata/30min?hide_event_type_details=1&hide_gdpr_banner=1';
const SCRIPT_SRC = 'https://assets.calendly.com/assets/external/widget.js';
const CSS_HREF = 'https://assets.calendly.com/assets/external/widget.css';

type CalendlyWindow = Window & { Calendly?: { initPopupWidget: (opts: { url: string }) => void } };

let loading: Promise<void> | null = null;

// Load Calendly only after intent, so the third-party widget does not slow every page view.
function loadCalendly(): Promise<void> {
  if ((window as CalendlyWindow).Calendly) return Promise.resolve();
  if (loading) return loading;
  if (!document.querySelector(`link[href="${CSS_HREF}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = CSS_HREF;
    document.head.appendChild(link);
  }
  loading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loading = null;
      script.remove();
      reject(new Error('Calendly script failed to load'));
    };
    document.body.appendChild(script);
  });
  return loading;
}

const CalendlyWidget = () => {
  const opening = useRef(false);
  const { locale } = useLocale();
  const label = locale === 'pt-BR' ? 'Agende uma demonstração!' : 'Schedule a demo!';
  const poweredBy = locale === 'pt-BR' ? 'Agendamento via Calendly' : 'powered by Calendly';

  const preload = () => {
    loadCalendly().catch(() => { /* The click can still open the booking page. */ });
  };

  const handleClick = async (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    if (opening.current) return;
    opening.current = true;
    try {
      await Promise.race([
        loadCalendly(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000)),
      ]);
      const calendly = (window as CalendlyWindow).Calendly;
      if (!calendly) throw new Error('Calendly missing');
      calendly.initPopupWidget({ url: CALENDLY_URL });
    } catch {
      window.location.href = CALENDLY_URL;
    } finally {
      opening.current = false;
    }
  };

  return (
    <div className="demo-badge fixed bottom-[15px] right-5 z-[9998]">
      <a
        href={CALENDLY_URL}
        onClick={handleClick}
        onPointerEnter={preload}
        onFocus={preload}
        onTouchStart={preload}
        data-ga-cta="schedule_demo"
        data-ga-location="calendly_badge"
        className="flex h-[45px] flex-col items-center justify-center rounded-[25px] bg-[#0069ff] px-[30px] text-center font-sans text-sm font-bold leading-tight text-white shadow-[0_2px_5px_rgba(0,0,0,0.25)] no-underline"
      >
        {label}
        <span className="block text-xs font-bold">{poweredBy}</span>
      </a>
    </div>
  );
};

export default CalendlyWidget;
